import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useCallback } from 'react';
import { useAtomCallback } from 'jotai/react/utils';
import { previewExecutionIdAtom } from '~/atoms/atoms';
import { api } from '~/trpc-extra/api';
import { SaveAiProgressDialog } from './SaveAiProgressDialog';

export const useSaveAiProgress = ({ testContentId }: { testContentId: string }) => {
  const trpcUtils = api.useUtils();
  const getPreviewExecutionId = useAtomCallback((get) => get(previewExecutionIdAtom));
  const { data: testContent } = api.testContent.get.useQuery(
    { id: testContentId },
    { refetchOnMount: false, refetchOnWindowFocus: false, enabled: !!testContentId },
  );
  const { mutateAsync: getStepPreviewTempContent } = api.testStep.getStepPreviewTempContent.useMutation();
  const { mutate: resetStepFromPreview } = api.testStep.resetStepFromPreview.useMutation({
    onSuccess: async () => {
      await Promise.allSettled([
        trpcUtils.testContent.get.invalidate({ id: testContentId }),
        trpcUtils.testContent.getCode.invalidate({ testContentId }),
      ]);
    },
    onError: (_) => {
      notifications.show({
        title: 'Error',
        message: 'Unable to reset step from preview',
        color: 'red',
      });
    },
  });

  const saveAiProgress = useCallback(
    async (stepId: string) => {
      const step = testContent?.steps.find((step) => step.id === stepId);

      const previewExecutionId = getPreviewExecutionId();
      if (!previewExecutionId) {
        return;
      }

      const stepPreviewTempContent = await getStepPreviewTempContent({
        stepId,
        previewExecutionId,
      });
      try {
        if (!stepPreviewTempContent) {
          return;
        }

        // We don't save AI progress for auth steps or empty new steps
        if (stepPreviewTempContent?.newOperations?.length === 0 || step?.mode === 'AUTH') {
          // Since we won't have a chance to save the AI progress, we need to reset the step from preview
          resetStepFromPreview({ stepId, previewExecutionId: stepPreviewTempContent?.previewExecutionId ?? '' });
          return;
        }

        // Auth step is hairy and we don't want to save it
        if (step && step.mode !== 'STRICT') {
          modals.open({
            size: 'md',
            centered: true,
            closeOnClickOutside: false,
            withCloseButton: false,
            children: (
              <SaveAiProgressDialog
                testContentId={testContentId}
                stepId={step.id}
                stepOrder={step.stepOrder}
                prevOperations={stepPreviewTempContent?.prevOperations ?? []}
                previewExecutionId={stepPreviewTempContent?.previewExecutionId ?? ''}
              />
            ),
          });
        }
      } catch (e) {
        resetStepFromPreview({ stepId, previewExecutionId: stepPreviewTempContent?.previewExecutionId ?? '' });
        console.error(e);
        notifications.show({
          title: 'Error',
          message: 'Unable to save AI progress',
          color: 'red',
        });
      }
    },
    [getPreviewExecutionId, getStepPreviewTempContent, resetStepFromPreview, testContent?.steps, testContentId],
  );
  return saveAiProgress;
};
